import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaArrowLeft, 
  FaUtensils, 
  FaFire, 
  FaTint, 
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaStickyNote,
  FaAppleAlt,
  FaDumbbell,
  FaClock,
  FaUser,
  FaSpinner,
  FaCheckCircle,
  FaFilter,
  FaSearch,
  FaDownload,
  FaExclamationCircle,
  FaClipboardList,
  FaLeaf,
  FaCoffee,
  FaEgg,
  FaCarrot,
  FaFish,
  FaBreadSlice,
  FaRegCalendarCheck,
  FaHeartbeat,
  FaBurn,
  FaRunning
} from 'react-icons/fa';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background || '#f8f9fa'};
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  position: relative;
`;

const BackButton = styled.button`
  background: ${({ theme }) => theme.colors.primary || '#291759'};
  border: none;
  border-radius: 50%;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(41, 23, 89, 0.3);

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark || '#1e0f45'};
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(41, 23, 89, 0.4);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary || '#292929'};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: ${({ theme }) => theme.colors.primary || '#291759'};
    border-radius: 3px;
  }
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: ${({ theme }) => theme.colors.primary || '#291759'};
  font-size: 1.2rem;
  gap: 1rem;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.error || '#f8d7da'};
  color: white;
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
  background: white;
  padding: 1.25rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  
  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  position: relative;
  min-width: 200px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.85rem 1rem 0.85rem 2.75rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border || '#e1e4e8'};
  font-size: 1rem;
  background: ${({ theme }) => theme.colors.background || '#f8f9fa'};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary || '#291759'};
    box-shadow: 0 0 0 3px rgba(41, 23, 89, 0.15);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.9rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const Tab = styled.button`
  background: ${props => props.active ? ({ theme }) => theme.colors.primary || '#291759' : 'white'};
  border: 2px solid ${props => props.active ? ({ theme }) => theme.colors.primary || '#291759' : ({ theme }) => theme.colors.border || '#e1e4e8'};
  color: ${props => props.active ? 'white' : ({ theme }) => theme.colors.textPrimary || '#292929'};
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  box-shadow: ${props => props.active ? '0 4px 10px rgba(41, 23, 89, 0.2)' : 'none'};

  &:hover {
    background: ${props => props.active ? ({ theme }) => theme.colors.primaryDark || '#1e0f45' : ({ theme }) => theme.colors.background || '#f8f9fa'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
`;

const PlansGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  }
`;

const PlanCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border || '#e1e4e8'};
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0,0,0,0.12);
    border-color: ${props => props.type === 'meal' 
      ? '#28a745' 
      : '#fd7e14'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  background: ${props => props.type === 'meal' 
    ? 'linear-gradient(135deg,rgb(0, 18, 119),rgb(0, 4, 89))' 
    : 'linear-gradient(135deg,rgb(0, 18, 119),rgb(0, 4, 89))'};
  color: white;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: -30px;
    right: -30px;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    z-index: 1;
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 2;
`;

const CardSubtitle = styled.div`
  margin-top: 0.35rem;
  font-size: 0.9rem;
  opacity: 0.9;
  color: white;
  position: relative;
  z-index: 2;
`;

const PlanStatusBadge = styled.div`
  background: rgba(255, 255, 255, 0.25);
  padding: 0.35rem 0.75rem;
  border-radius: 2rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  backdrop-filter: blur(4px);
  position: relative;
  z-index: 2;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
`;

const MealSection = styled.div`
  background: #f9fff9;
  border: 1px solid #e0f0e0;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color:rgb(19, 3, 122);
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.1);
  }
`;

const WorkoutSection = styled.div`
  background: #fff9f2;
  border: 1px solid #f0e0d0;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color:rgb(4, 31, 162);
    box-shadow: 0 4px 8px rgba(253, 126, 20, 0.1);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  color: ${props => props.type === 'meal' ? '#28a745' : '#fd7e14'};
  font-weight: 600;
  font-size: 1.1rem;
`;

const MealTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f9fafb;
  border-top: 1px solid ${({ theme }) => theme.colors.border || '#e1e4e8'};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const NutritionalInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const NutritionBadge = styled.div`
  background: ${props => props.type === 'meal' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(253, 126, 20, 0.1)'};
  color: ${props => props.type === 'meal' ? '#28a745' : '#fd7e14'};
  padding: 0.4rem 0.75rem;
  border-radius: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const EmptyState = styled.div`
  text-align: center;
  background: white;
  border-radius: 16px;
  padding: 4rem 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
`;

const EmptyStateIcon = styled.div`
  font-size: 5rem;
  color: ${({ theme }) => theme.colors.border || '#e1e4e8'};
  margin-bottom: 1.5rem;
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.textPrimary || '#292929'};
`;

const EmptyStateText = styled.p`
  margin: 0 auto;
  max-width: 500px;
  color: ${({ theme }) => theme.colors.textSecondary || '#666'};
  font-size: 1.1rem;
  line-height: 1.5;
`;

const CalendarView = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  margin-bottom: 2.5rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CalendarTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.textPrimary || '#292929'};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    color: ${({ theme }) => theme.colors.primary || '#291759'};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const PaginationButton = styled.button`
  background: ${props => props.disabled 
    ? '#f0f0f0' 
    : ({ theme }) => theme.colors.primary || '#291759'};
  color: ${props => props.disabled ? '#a0a0a0' : 'white'};
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.25rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-weight: 600;
  
  &:not(:disabled):hover {
    background: ${({ theme }) => theme.colors.primaryDark || '#1e0f45'};
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  }
`;

const MealDetail = styled.div`
  margin-bottom: 0.5rem;
  line-height: 1.5;
  
  strong {
    color: #333;
    display: block;
    margin-bottom: 0.35rem;
  }
  
  span {
    color: #555;
  }
`;

const NotesSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 10px;
  border-left: 3px solidrgb(73, 7, 255);
`;

const NotesTitle = styled.div`
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
`;

const NotesContent = styled.p`
  margin: 0.5rem 0 0 0;
  color: #666;
  line-height: 1.5;
`;

const MealIcon = ({ mealType }) => {
  switch (mealType.toLowerCase()) {
    case 'breakfast':
      return <FaEgg />;
    case 'lunch':
      return <FaCarrot />;
    case 'dinner':
      return <FaFish />;
    case 'snacks':
      return <FaBreadSlice />;
    default:
      return <FaUtensils />;
  }
};

const WorkoutIcon = ({ workoutType }) => {
  if (!workoutType) return <FaDumbbell />;
  
  switch (workoutType.toLowerCase()) {
    case 'cardio':
      return <FaHeartbeat />;
    case 'strength':
      return <FaDumbbell />;
    case 'hiit':
    case 'mixed':
      return <FaBurn />;
    default:
      return <FaRunning />;
  }
};

const MyDailyPlans = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [dailyPlans, setDailyPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [userPlan, setUserPlan] = useState(null);
  
  const plansPerPage = 6;
  
  useEffect(() => {
    if (currentUser) {
      loadDailyPlans();
      fetchUserPlan();
    }
  }, [currentUser]);
  
  const loadDailyPlans = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const plansRef = collection(db, 'users', currentUser.uid, 'dailyPlans');
      const plansQuery = query(plansRef, orderBy('date', 'asc'));
      const plansSnapshot = await getDocs(plansQuery);
      
      const plansData = [];
      plansSnapshot.forEach(doc => {
        plansData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      plansData.sort((a, b) => {
        const dateA = a.date instanceof Date ? a.date : new Date(a.date);
        const dateB = b.date instanceof Date ? b.date : new Date(b.date);
        return dateA - dateB;
      });
      
      setDailyPlans(plansData);
      setLoading(false);
      
    } catch (err) {
      console.error('Error loading daily plans:', err);
      setError('Failed to load your daily plans. Please try again later.');
      setLoading(false);
    }
  };
  
  const fetchUserPlan = async () => {
    if (!currentUser) return;
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const selectedPlanId = userData.selectedPlanId;
        
        if (selectedPlanId) {
          const planRef = doc(db, 'plans', selectedPlanId);
          const planDoc = await getDoc(planRef);
          
          if (planDoc.exists()) {
            setUserPlan({
              ...planDoc.data(),
              id: selectedPlanId,
              selectedAt: userData.planSelectedAt || null
            });
          }
        }
      }
    } catch (err) {
      console.error('Error fetching user plan:', err);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };
  
  const getFilteredPlans = () => {
    return dailyPlans.filter(plan => {
      if (activeTab !== 'all' && plan.type !== activeTab) return false;
      
      if (searchTerm && !plan.notes?.toLowerCase().includes(searchTerm.toLowerCase())) {
        if (plan.type === 'meal') {
          const breakfastMatch = plan.breakfast?.foods?.toLowerCase().includes(searchTerm.toLowerCase());
          const lunchMatch = plan.lunch?.foods?.toLowerCase().includes(searchTerm.toLowerCase());
          const dinnerMatch = plan.dinner?.foods?.toLowerCase().includes(searchTerm.toLowerCase());
          if (!breakfastMatch && !lunchMatch && !dinnerMatch) return false;
        } else if (plan.type === 'workout') {
          if (!plan.exercises?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        }
      }
      
      if (selectedDate) {
        const planDate = new Date(plan.date);
        const filterDate = new Date(selectedDate);
        if (planDate.toDateString() !== filterDate.toDateString()) return false;
      }
      
      return true;
    });
  };
  
  const handlePlanClick = (planId) => {
    navigate(`/customer/plans/${planId}`);
  };
  
  const getPartnerInitials = (name) => {
    if (!name) return 'P';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };
  
  const filteredPlans = getFilteredPlans();
  const totalPages = Math.ceil(filteredPlans.length / plansPerPage);
  const currentPlans = filteredPlans.slice(
    (currentPage - 1) * plansPerPage, 
    currentPage * plansPerPage
  );
  
  const groupPlansByDate = () => {
    const grouped = {};
    filteredPlans.forEach(plan => {
      const dateStr = new Date(plan.date).toDateString();
      if (!grouped[dateStr]) {
        grouped[dateStr] = [];
      }
      grouped[dateStr].push(plan);
    });
    return grouped;
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setCurrentPage(1);
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  
  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate('/customer/dashboard')}>
          <FaArrowLeft />
        </BackButton>
        <Title>My Daily Plans</Title>
      </Header>
      
      {error && (
        <ErrorMessage>
          <FaExclamationCircle />
          {error}
        </ErrorMessage>
      )}
      
      {loading ? (
        <LoadingContainer>
          <FaSpinner />
          Loading your daily plans...
        </LoadingContainer>
      ) : (
        <>
          <FiltersBar>
            <SearchContainer>
              <SearchIcon>
                <FaSearch />
              </SearchIcon>
              <SearchInput 
                type="text" 
                placeholder="Search in your plans..." 
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </SearchContainer>
            
            <input 
              type="date" 
              value={selectedDate}
              onChange={handleDateChange}
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '10px',
                border: '1px solid #e1e4e8',
                minWidth: '200px'
              }}
            />
          </FiltersBar>
          
          <TabsContainer>
            <Tab active={activeTab === 'all'} onClick={() => handleTabChange('all')}>
              <FaClipboardList />
              All Plans
            </Tab>
            <Tab active={activeTab === 'meal'} onClick={() => handleTabChange('meal')}>
              <FaUtensils />
              Meal Plans
            </Tab>
            <Tab active={activeTab === 'workout'} onClick={() => handleTabChange('workout')}>
              <FaDumbbell />
              Workout Plans
            </Tab>
          </TabsContainer>
          
          {userPlan && (
            <CalendarView>
              <CalendarHeader>
                <CalendarTitle>
                  <FaRegCalendarCheck />
                  Your Current Plan: {userPlan.name}
                </CalendarTitle>
              </CalendarHeader>
              <p style={{ color: '#666' }}>
                These daily meal and workout plans are created by your nutritionist to help you achieve your goals.
                They are personalized based on your current plan.
              </p>
            </CalendarView>
          )}
          
          {filteredPlans.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <FaClipboardList />
              </EmptyStateIcon>
              <EmptyStateTitle>No Plans Found</EmptyStateTitle>
              <EmptyStateText>
                {searchTerm || selectedDate ? 
                  'No plans match your search criteria. Try adjusting your filters.' : 
                  'You don\'t have any daily plans yet. Your nutritionist will create personalized meal and workout plans for you soon.'}
              </EmptyStateText>
            </EmptyState>
          ) : (
            <>
              <PlansGrid>
                {currentPlans.map(plan => (
                  <PlanCard 
                    key={plan.id} 
                    type={plan.type}
                    onClick={() => handlePlanClick(plan.id)}
                  >
                    <CardHeader type={plan.type}>
                      <div>
                        <CardTitle>
                          {plan.type === 'meal' ? <FaUtensils /> : <FaDumbbell />}
                          {formatDate(plan.date)}
                        </CardTitle>
                        <CardSubtitle>
                          Created by {plan.partnerName || 'Your Nutritionist'}
                        </CardSubtitle>
                      </div>
                      
                      <PlanStatusBadge>
                        {plan.type === 'meal' ? 'MEAL PLAN' : 'WORKOUT PLAN'}
                      </PlanStatusBadge>
                    </CardHeader>
                    
                    <CardContent>
                      {plan.type === 'meal' && (
                        <>
                          {plan.breakfast && (plan.breakfast.foods || plan.breakfast.calories) && (
                            <MealSection>
                              <SectionHeader type="meal">
                                <MealTitle>
                                  <MealIcon mealType="breakfast" />
                                  Breakfast
                                </MealTitle>
                                <span style={{ fontSize: '0.9rem' }}>{plan.breakfast.time || '08:00'}</span>
                              </SectionHeader>
                              
                              {plan.breakfast.foods && (
                                <MealDetail>
                                  <strong>Foods</strong>
                                  <span>{plan.breakfast.foods}</span>
                                </MealDetail>
                              )}
                              
                              {plan.breakfast.calories && (
                                <NutritionalInfo>
                                  <NutritionBadge type="meal">
                                    <FaFire />
                                    {plan.breakfast.calories} calories
                                  </NutritionBadge>
                                </NutritionalInfo>
                              )}
                            </MealSection>
                          )}
                          
                          {plan.lunch && (plan.lunch.foods || plan.lunch.calories) && (
                            <MealSection>
                              <SectionHeader type="meal">
                                <MealTitle>
                                  <MealIcon mealType="lunch" />
                                  Lunch
                                </MealTitle>
                                <span style={{ fontSize: '0.9rem' }}>{plan.lunch.time || '13:00'}</span>
                              </SectionHeader>
                              
                              {plan.lunch.foods && (
                                <MealDetail>
                                  <strong>Foods</strong>
                                  <span>{plan.lunch.foods}</span>
                                </MealDetail>
                              )}
                              
                              {plan.lunch.calories && (
                                <NutritionalInfo>
                                  <NutritionBadge type="meal">
                                    <FaFire />
                                    {plan.lunch.calories} calories
                                  </NutritionBadge>
                                </NutritionalInfo>
                              )}
                            </MealSection>
                          )}
                          
                          {plan.totalCalories && (
                            <div style={{ 
                              padding: '0.75rem 1rem',
                              background: '#e8f5e8', 
                              borderRadius: '10px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginTop: '1rem'
                            }}>
                              <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#28a745' }}>
                                <FaFire />
                                Total Calories:
                              </span>
                              <span style={{ fontWeight: '700', color: '#28a745' }}>{plan.totalCalories}</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {plan.type === 'workout' && (
                        <>
                          {plan.restDay ? (
                            <div style={{ 
                              textAlign: 'center', 
                              padding: '2rem',
                              background: '#f8f9fa',
                              borderRadius: '12px',
                              color: '#666'
                            }}>
                              <h4 style={{ color: '#fd7e14', margin: '0 0 0.75rem 0' }}>Rest Day</h4>
                              <p style={{ margin: '0' }}>Take a well-deserved break today!</p>
                            </div>
                          ) : (
                            <WorkoutSection>
                              <SectionHeader type="workout">
                                <MealTitle>
                                  <WorkoutIcon workoutType={plan.workoutType} />
                                  {plan.workoutType?.charAt(0).toUpperCase() + plan.workoutType?.slice(1) || 'Workout'}
                                </MealTitle>
                              </SectionHeader>
                              
                              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                                {plan.duration && (
                                  <NutritionBadge type="workout">
                                    <FaClock />
                                    {plan.duration} minutes
                                  </NutritionBadge>
                                )}
                                
                                {plan.caloriesBurned && (
                                  <NutritionBadge type="workout">
                                    <FaFire />
                                    {plan.caloriesBurned} calories burned
                                  </NutritionBadge>
                                )}
                              </div>
                              
                              {plan.exercises && (
                                <MealDetail>
                                  <strong>Exercises</strong>
                                  <span style={{ whiteSpace: 'pre-line' }}>{plan.exercises}</span>
                                </MealDetail>
                              )}
                              
                              {plan.sets && plan.reps && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '1rem' }}>
                                  <div>
                                    <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#333', fontSize: '0.9rem' }}>Sets</strong>
                                    <span style={{ color: '#555' }}>{plan.sets}</span>
                                  </div>
                                  
                                  <div>
                                    <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#333', fontSize: '0.9rem' }}>Reps</strong>
                                    <span style={{ color: '#555' }}>{plan.reps}</span>
                                  </div>
                                  
                                  {plan.weight && (
                                    <div>
                                      <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#333', fontSize: '0.9rem' }}>Weight</strong>
                                      <span style={{ color: '#555' }}>{plan.weight}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </WorkoutSection>
                          )}
                        </>
                      )}
                      
                      {plan.notes && (
                        <NotesSection>
                          <NotesTitle>
                            <FaStickyNote />
                            Notes
                          </NotesTitle>
                          <NotesContent>{plan.notes}</NotesContent>
                        </NotesSection>
                      )}
                    </CardContent>
                    
                    <CardFooter>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaCalendarAlt />
                        {new Date(plan.date).toLocaleDateString()}
                      </span>
                      
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaUser />
                        {plan.partnerName || 'Nutritionist'}
                      </span>
                    </CardFooter>
                  </PlanCard>
                ))}
              </PlansGrid>
              
              {totalPages > 1 && (
                <PaginationContainer>
                  <PaginationButton 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  >
                    <FaChevronLeft /> Previous
                  </PaginationButton>
                  
                  <span style={{ color: '#666', fontWeight: '500' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <PaginationButton 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  >
                    Next <FaChevronRight />
                  </PaginationButton>
                </PaginationContainer>
              )}
            </>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default MyDailyPlans;